;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:synergiefrontends":true});}catch(e){}}();import React, { useEffect, useRef, useState } from 'react';

import { useRouter } from 'next/navigation';
import { groupBy } from 'lodash';
import { Autocomplete, GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { Button, Col, Container, Input, Row } from 'reactstrap';
import AgencyTeaser from 'shared/components/AgencyTeaser';
import AgencyMapMarker from 'shared/components/AgencyMapMarker';
import Loading from 'shared/components/Loading';
import AgencyNotFound from 'shared/components/svg/AgencyNotFound';
import useWindowWidth from 'shared/hooks/useWindowWidth';
import { mapCenter, mapZoom } from 'shared/utils/mapPositioning';
import { getI18nUtils } from '../i18n/i18n-utils';
import { Locale, SynergiecareersDictionary, SynergieconstructDictionary, SynergiejobsDictionary } from '../i18n/i18n-config';

type SearchType = { place: string; lat: string; lng: string };
type dayNumber = '0' | '1' | '2' | '3' | '4' | '5' | '6';
type openingHours = { [key in dayNumber]: { start: string; end: string }[] };
export type Agency = {
  _id?: string;
  id?: string;
  title: string;
  address1: string;
  address2?: string;
  apps?: string[];
  postalCode: string;
  city: string;
  email: string;
  telephone: string;
  fax?: string;
  created: number;
  updated: number;
  lat: number;
  lng: number;
  uri?: string;
  language?: string;
  openingHours?: openingHours;
  appointmentUrl?: string;
  reviewWidgetScriptUrl?: string | null;
};

const defaultRadius = '25000';

const Agencies = ({
  agencies,
  dictionary,
  locale,
  query,
}: {
  agencies: Agency[];
  dictionary: SynergieconstructDictionary | SynergiejobsDictionary | SynergiecareersDictionary;
  locale: Locale;
  query: { place: string; lat: string; lng: string; radius: string };
}): JSX.Element => {
  const { formatMessage } = getI18nUtils(dictionary);
  const router = useRouter();
  const width = useWindowWidth();
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: 'AIzaSyBupFH0E8sisWkj87o6MEYxFRueNVm9RuY',
    libraries: ['places'],
  });
  const [autoComplete, setAutoComplete] = useState<google.maps.places.Autocomplete | null>(null);
  const [agencySelected, setAgencySelected] = useState<string | null>(null);
  const [search, setSearch] = useState<SearchType | null>(null);
  const [radius, setRadius] = useState<string>(defaultRadius);
  const onMobile = width && width < 768;
  const center = mapCenter(agencies);
  const zoom = onMobile ? mapZoom(agencies) - 1 : mapZoom(agencies);
  const groupedAgencies = groupBy(agencies, (item) => `${item.lat}+${item.lng}`);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const { place, lat, lng, radius: queryRadius } = query;

  useEffect(() => {
    if (inputRef.current) inputRef.current.value = '';
    setRadius(defaultRadius);
    setSearch(null);
    setAgencySelected(null);
  }, [locale]);

  useEffect(() => {
    if (isLoaded) inputRef.current = document.getElementById('autocomplete') as HTMLInputElement;
    if (isLoaded && place && lat && lng && queryRadius && inputRef?.current) {
      setSearch({ place, lat, lng });
      setRadius(queryRadius);
      inputRef.current.value = query.place;
    }
  }, [isLoaded]);

  const onPlaceChanged = () => {
    if (autoComplete === null) return;
    const addressObject = autoComplete.getPlace();
    if (addressObject.geometry && inputRef?.current) {
      const { lat: addressObjectLat, lng: addressObjectLng } = addressObject.geometry.location as google.maps.LatLng;
      const newPlace = inputRef.current.value;
      const newRadius = search ? defaultRadius : radius;
      setSearch({
        place: newPlace,
        lat: addressObjectLat().toString(),
        lng: addressObjectLng().toString(),
      });
      setRadius(newRadius);
      const queryParams = {
        place: newPlace,
        lat: String(addressObjectLat()),
        lng: String(addressObjectLng()),
        radius: newRadius,
      };
      const queryParamString = new URLSearchParams(queryParams).toString();
      router.push(`/${locale}/agencies?${queryParamString}`);
    }
  };

  const onRadiusChanged = (newRadius: string) => {
    setRadius(newRadius);
    const queryParams = {
      ...(search?.place && { place: search.place }),
      ...(search?.lat && { lat: search.lat }),
      ...(search?.lng && { lng: search.lng }),
      radius: newRadius,
    };
    const queryParamString = new URLSearchParams(queryParams).toString();
    if (search) router.push(`/${locale}/agencies?${queryParamString}`);
  };

  const reset = (e) => {
    e.preventDefault();
    setRadius(defaultRadius);
    setSearch(null);
    setAgencySelected(null);
    if (inputRef?.current) inputRef.current.value = '';
    router.push(`/${locale}/agencies/`);
  };

  return (
    <Container className="agenciesPage mt-5">
      <Row style={{ margin: 'auto' }}>
        <Col xs={12} lg={7} className="p-0 m-0 mb-3">
          <h1 className="text-center text-lg-start mt-1 fs-2">{formatMessage({ id: 'agencies-h1' })}</h1>
        </Col>
        <Col xs={12} lg={5} className="p-0 m-0">
          <Row className="d-flex justify-content-end align-items-center" style={{ margin: 'auto' }}>
            {/* PLACE */}
            <Col xs={12} lg={6} className="p-0 m-0 mb-3">
              {isLoaded && (
                <Autocomplete
                  onPlaceChanged={onPlaceChanged}
                  onLoad={(c) => setAutoComplete(c)}
                  types={['(regions)']}
                  restrictions={{ country: 'be' }}
                >
                  <Input
                    type="text"
                    placeholder={formatMessage({
                      id: 'agencies-search-placeholder',
                    })}
                    aria-label="search agencies"
                    id="autocomplete"
                    autoComplete="off"
                  />
                </Autocomplete>
              )}
            </Col>
            {/* RADIUS */}
            <Col xs={12} lg={4} className="p-0 m-0 mb-3 ms-2">
              <Input
                type="select"
                name="radius"
                id="filterSelect"
                value={radius}
                onChange={(e) => onRadiusChanged(e.target.value)}
              >
                <option value="10000">10 km</option>
                <option value="25000">25 km</option>
                <option value="50000">50 km</option>
                <option value="75000">75 km</option>
                <option value="100000">100 km</option>
              </Input>
            </Col>
            {/* RESET BUTTON */}
            {search && (
              <Col xs={12} lg={1} className="p-0 m-0 mb-3 ms-2">
                <Button type="button" onClick={reset} className="w-100 m-0 p-1" style={{ height: '38px' }}>
                  <span className="d-none d-lg-inline-block">&#10005;</span>
                  <span className="d-lg-none">{formatMessage({ id: 'clear_all_filters' })}</span>
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
      {isLoaded ? (
        <GoogleMap
          mapContainerClassName="map-container"
          center={center}
          zoom={zoom}
          options={{ disableDefaultUI: true, zoomControl: true }}
          onClick={() => setAgencySelected(null)}
        >
          {Object.keys(groupedAgencies).map((agencyGroup) => (
            <AgencyMapMarker
              dictionary={dictionary}
              key={groupedAgencies[agencyGroup][0].id}
              agencyGroup={groupedAgencies[agencyGroup]}
              agencySelected={agencySelected}
              setAgencySelected={setAgencySelected}
              prependBrandName
            />
          ))}
        </GoogleMap>
      ) : (
        <Loading />
      )}
      {agencies.length === 0 && search && (
        <Row className="m-5">
          <Col
            xs={{ size: 12, order: 2 }}
            lg={{ size: 6, order: 1 }}
            className="d-flex justify-content-center align-items-center"
          >
            <div>
              <h2>{formatMessage({ id: 'pagenav-agencies' })}</h2>
              <p>{formatMessage({ id: 'agencies_not_found' }, { place: search.place, radius: Number(radius) / 1000 })}</p>
              <Button color="primary" onClick={(e) => reset(e)}>
                {formatMessage({ id: 'clear_all_filters' })}
              </Button>
            </div>
          </Col>
          <Col
            xs={{ size: 12, order: 1 }}
            lg={{ size: 6, order: 2 }}
            className="d-flex justify-content-center align-items-center"
          >
            <AgencyNotFound />
          </Col>
        </Row>
      )}
      <Row>
        {agencies.map((agency) => (
          <Col md={6} lg={4} key={agency.id} className=" py-3">
            <AgencyTeaser locale={locale} agency={agency} dictionary={dictionary} moreInfo prependBrandName />
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export default Agencies;
